import { lazy } from "react";

/**
 * moduleRegistry.js
 *
 * THE single source of truth mapping a module id (a plain string that
 * comes straight out of the login response, e.g. "appointments") to:
 *   - a human label (for the sidebar)
 *   - a route path (for the router)
 *   - a lazily-loaded page component
 *
 * Bootstrap, the Router and the Sidebar all read from this registry.
 * None of them contain "if module === 'appointments'" branching —
 * they just loop over whatever module ids the system config lists and
 * look each one up here. Adding module #6 means adding one entry here,
 * nothing else in the app changes.
 */
const sameLabel = (keys, value) =>
  Object.fromEntries(keys.map(k => [k, value]));

export const MODULE_REGISTRY = {
  //comen use 
  acceuil: {
    id: "acceuil",
    label: "Acceuil",
    path: "ma",
    icon: "house",
    component: lazy(() => import("./Main/mainPage.jsx")),
  },
  demmandes: {
    id: "demmandes",
    label: {
      default : "Demmandes",
      ...sameLabel(["magazin", "restaurant","transporteur"], "Commandes"),
      docteur : "Rendy-vous",
      ...sameLabel(["garderie", "lycee","universite"], "Inscription"),
      gym : "Souscription",
    },
    path: "rq",
    icon: "calendar2-check",
    component: lazy(() => import("./Requests/requestPage.js")),
    submodules: [
      {
        id: "reqinfo",
        label: "info",
        path: "info/:CID/:reqGenre",
        component: lazy(() => import("./Requests/requestInfo.js")),
      },
      {
        id: "calendrier",
        label: "info",
        path: "calendrier",
        component: lazy(() => import("./Requests/calendar.js")),
      },
    ],
  },
  client: {
    id: "client",
    label: {
      default : "Client",
      ...sameLabel(["docteur", "labo","clinique","centreMD", "infirmier"], "Patient"),
      garderie : "Eleves",
      gym : "Membres",
      autoecole : "Condidat",
      hotels : "Invitee",
    }, 
    path: "cl",
    icon: "people",
    component: lazy(() => import("./Patient/patientPage.js")),
    submodules: [
      {
        id: "ajouter",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Patient/ajouterPatient.js")),
      },
      {
        id: "info",
        label: "Info facture",
        path: "info/:CID",
        component: lazy(() => import("./Patient/patientInfo.js")),
      },
    ],
  },
  equipe: {
    id: "equipe",
    label: "Equipe",
    path: "tm",
    icon: "person-bounding-box",
    component: lazy(() => import("./Team/teamPage.js")),
    submodules: [
      {
        id: "ajouter",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Team/ajouterTeam.js")),
      },
      {
        id: "info",
        label: "Info facture",
        path: "info/:TID",
        component: lazy(() => import("./Team/teamInfo.js")),
      },
      {
        id: "poste",
        label: "Info facture",
        path: "postes",
        component: lazy(() => import("./Team/teamPoste.js")),
      },
      {
        id: "avances",
        label: "Info facture",
        path: "avances",
        component: lazy(() => import("./Team/teamAvance.js")),
      },
      {
        id: "presence",
        label: "Info facture",
        path: "presence",
        component: lazy(() => import("./Team/demandeTeam.js")),
      },
    ],
  },
  finance: {
    id: "finance",
    label: "Finance",
    path: "finance",
    icon: "cash-coin",
    component: lazy(() => import("./Finance/financePage.js")),
    submodules: [
      {
        id: "financeStat",
        label: "Stat",
        path: "stat",
        component: lazy(() => import("./Finance/financeStat.jsx")),
      },
    ],
  },

  //docteur 
  seances_docteur: {
    id: "seances_docteur",
    label: "Seances",
    path: "sa",
    icon: "stopwatch",
    component: lazy(() => import("./Seance/docteur/seancePage.js")),
    submodules: [
      {
        id: "ajouter_seances_docteur",
        label: "Info Seance",
        path: "ajouter",
        component: lazy(() => import("./Seance/docteur/ajoutreSeance.js")),
      },
      {
        id: "seances_docteur_info",
        label: "Info Seance",
        path: "info/:SID",
        component: lazy(() => import("./Seance/docteur/infoSeance.js")),
      },
      {
        id: "seances_docteur_resumer",
        label: "Info Seance",
        path: "resumer",
        component: lazy(() => import("./Seance/docteur/resumerSeance.js")),
      },
      
      //...
      {
        id: "tarification",
        label: "Info facture",
        path: "tarif",
        component: lazy(() => import("./Tarification/forfaitPage.js")),
      },
    ],
  },
  ordonance: {
    id: "ordonance",
    label: "Ordonance",
    path: "or",
    icon: "receipt-cutoff",
    component: lazy(() => import("./Ordonances/ordonancePage.js")),
    submodules: [
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "ajouter-c",
        component: lazy(() => import("./Ordonances/ajouterOrdonance.js")),
      },
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "info/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceInfo.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "modifier/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceEdit.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "sk",
        component: lazy(() => import("./Stock/docteur/StockPage.js")),
      },
    ]
  },
  rapport: {
    id: "rapport",
    label: "Rapport",
    path: "rp",
    icon: "file-earmark-easel",
    component: lazy(() => import("./Rapport/rapportPage.js")),
    submodules: [
      {
        id: "ajouter_rapport",
        label: "Info Seance",
        path: "ajouter",
        params : {type : 'ajouter'}, 
        component: lazy(() => import("./Rapport/addRapport.js")),
      },
      {
        id: "ajouter_rapport",
        label: "Info Seance",
        path: "info/:RPID",
        component: lazy(() => import("./Rapport/rapportInfo.js")),
      },
      {
        id: "ajouter_rapport",
        label: "Info Seance",
        path: "modifier/:RPID",
        params : {type : 'modier'}, 
        component: lazy(() => import("./Rapport/addRapport.js")),
      },
    ]
  },

  //labo
  analyses: {
    id: "analyses",
    label: "Analyse",
    path: "analyse",
    icon: "flask",
    component: lazy(() => import("./Ordonances/ordonancePage.js")),
    submodules: [
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "ajouter-c",
        component: lazy(() => import("./Ordonances/ajouterOrdonance.js")),
      },
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "info/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceInfo.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "modifier/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceEdit.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "sk",
        component: lazy(() => import("./Stock/docteur/StockPage.js")),
      },
    ]
  },

  //infirmier
  calendrier : {
    id: "calendrier",
    label: "Calendirer",
    path: "calendrier",
    icon: "calendar-week",
    component: lazy(() => import("./Finance/financePage.js")),
    submodules: [
      {
        id: "financeStat",
        label: "Stat",
        path: "stat",
        component: lazy(() => import("./Finance/financeStat.jsx")),
      },
    ],
  },
  visites : {
    id: "visites",
    label: "Visites",
    path: "visites",
    icon: "sign-merge-right",
    component: lazy(() => import("./Finance/financePage.js")),
    submodules: [
      {
        id: "financeStat",
        label: "Stat",
        path: "stat",
        component: lazy(() => import("./Finance/financeStat.jsx")),
      },
    ],
  },
  
  //autoecole
  abonnement_autoecole: {
    id: "abonnement",
    label: "Abonnement",
    path: "ab",
    icon: "person-vcard",
    component: lazy(() => import("./Abonnement/autoecole/abonnemmentPage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Abonnement/autoecole/ajoutreAbonnement.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:FID",
        component: lazy(() => import("./Abonnement/autoecole/infoAbonnemment.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "resumer",
        component: lazy(() => import("./Abonnement/autoecole/resumerAbonnemment.js")),
      },
      {
        id: "tarification",
        label: "Info facture",
        path: "tarif",
        component: lazy(() => import("./Tarification/forfaitPage.js")),
      },
    ],
  },
  seances_autoecole: {
    id: "seances_docteur",
    label: "Seances",
    path: "sa",
    icon: "stopwatch",
    component: lazy(() => import("./Seance/autoecole/seancePage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:SID",
        component: lazy(() => import("./Seance/autoecole/infoSeance.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter-p",
        component: lazy(() => import("./Seance/autoecole/ajoutreSeancePra.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter-t",
        component: lazy(() => import("./Seance/autoecole/ajoutreSeanceTheo.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "resumer",
        component: lazy(() => import("./Seance/autoecole/resumerSeance.js")),
      },
      //...
      {
        id: "tarification",
        label: "Info facture",
        path: "tarif",
        component: lazy(() => import("./Tarification/forfaitPage.js")),
      },
    ],
  },
  voiture_autoecole: {
    id: "voiture",
    label: "Voitures",
    path: "vo",
    icon: "car-front",
    component: lazy(() => import("./Voitures/autoecole/classePage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:code",
        component: lazy(() => import("./Voitures/autoecole/classeInfo.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Voitures/autoecole/addClasse.js")),
      },
    ]
  },
  //parking 
  places : {
    id: "places",
    label: "Places",
    path: "places",
    icon: "columns-gap",
    component: lazy(() => import("./Voitures/autoecole/classePage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:code",
        component: lazy(() => import("./Voitures/autoecole/classeInfo.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Voitures/autoecole/addClasse.js")),
      },
    ]
  },

  //gym
  abonnement_gym: {
    id: "abonnement",
    label: "Abonnement",
    path: "ab",
    icon: "person-vcard",
    component: lazy(() => import("./Abonnement/gym/abonnemmentPage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Abonnement/gym/ajoutreAbonnement.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:FID",
        component: lazy(() => import("./Abonnement/gym/infoAbonnemment.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "resumer",
        component: lazy(() => import("./Abonnement/gym/resumerAbonnemment.js")),
      },
      {
        id: "tarification",
        label: "Info facture",
        path: "tarif",
        component: lazy(() => import("./Tarification/forfaitPage.js")),
      },
    ],
  },
  seances_gym: {
    iid: "seances_gym",
    label: "Seances",
    path: "sa",
    icon: "stopwatch",
    component: lazy(() => import("./Seance/gym/seancePage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:CID",
        component: lazy(() => import("./Seance/gym/seanceInfo.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Seance/gym/ajouterSeance.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "resumer",
        component: lazy(() => import("./Seance/gym/seanceBons.js")),
      },
    ],
  },
  equipemment: {
    id: "equipemment",
    label: "Equipemment",
    path: "eq",
    icon: "boxes",
    component: lazy(() => import("./Equipemment/equipemmentPage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Equipemment/addEquipemment.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:code",
        component: lazy(() => import("./Equipemment/equipemmentInfo.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "famille",
        component: lazy(() => import("./Equipemment/famille.js")),
      },
    ]
  },

  //restaurant
  reservations: {
    id: "reservations",
    label: "Reservation",
    path: "rs",
    icon: "alarm",
    component: lazy(() => import("./Reservation/restaurant/requestPage.js")),
    submodules: [
      {
        id: "calendrier",
        label: "info",
        path: "calendrier",
        component: lazy(() => import("./Requests/calendar.js")),
      },
    ]
  },
  menu: {
    id: "menu",
    label: "Menu",
    path: "mu",
    icon: "menu-down",
    component: lazy(() => import("./Menu/menuPage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Menu/addPlat.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:code",
        component: lazy(() => import("./Menu/PlatInfo.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "famille",
        component: lazy(() => import("./Menu/famille.js")),
      },
      {
        id: "Stock",
        label: "Info facture",
        path: "stock",
        component: lazy(() => import("./Stock/restaurant/stockPage.js")),
      },
    ]
  },
  caisses_restaurant: {
    id: "caisses",
    label: "Caisses",
    path: "ca",
    icon: "pc-display-horizontal",
    component: lazy(() => import("./Caisses/restaurant/caissePage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Caisses/restaurant/ajouterCaisse.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:CID",
        component: lazy(() => import("./Caisses/restaurant/caisseInfo.js")),
      },
    ]
  },
  facture_restaurant: {
    id: "facture",
    label: "Facture",
    path: "facture",
    icon: "receipt-cutoff",
    component: lazy(() => import("./Factures/restaurant/facturePage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Factures/restaurant/ajoutreFacture.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:FID",
        component: lazy(() => import("./Factures/restaurant/infoFacture.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "resumer",
        component: lazy(() => import("./Factures/restaurant/resumerFactures.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "tables",
        component: lazy(() => import("./Table/tablePage.js")),
      },
    ],
  },

  //gaerdeie
  eleves: {
    id: "abonnement",
    label: { 
      default : "Eleves",
      universite : "Etudiant",
      garderie : "Enfant",
    },
    path: "el",
    icon: "people",
    component: lazy(() => import("./Eleve/ElevePage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Eleve/ajouterEleve.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:CLID",
        component: lazy(() => import("./Eleve/EleveInfo.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "avertissemment",
        component: lazy(() => import("./Eleve/ajouterAvertissemment.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "retenue",
        component: lazy(() => import("./Eleve/ajouterRetenue.js")),
      },
    ]
  },
  abonnement_garderie: {
    id: "abonnement",
    label: "Abonnement",
    path: "ab",
    icon: "person-vcard",
    component: lazy(() => import("./Abonnement/garderie/abonnemmentPage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Abonnement/garderie/ajoutreAbonnement.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:FID",
        component: lazy(() => import("./Abonnement/garderie/infoAbonnemment.js")),
      },
      // {
      //   id: "factureInfo",
      //   label: "Info facture",
      //   path: "resumer",
      //   component: lazy(() => import("./Abonnement/garderie/resumerAbonnemment.js")),
      // },
      {
        id: "tarification",
        label: "Info facture",
        path: "tarif",
        component: lazy(() => import("./Tarification/forfaitPage.js")),
      },
    ],
  },
  classes: {
    id: "classes",
    label: "Classes",
    path: "classe",
    icon: "ui-radios-grid",
    component: lazy(() => import("./Classes/classePage.js")),
     submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Classes/addClasse.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:CLID",
        component: lazy(() => import("./Classes/classeInfo.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:CLID",
        component: lazy(() => import("./Classes/classeInfo.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "niveaux",
        component: lazy(() => import("./Classes/niveaux.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "emploi",
        component: lazy(() => import("./Classes/ajouterEmploi.js")),
      },
    ]
  },
  seances_garderie: {
    id: "seances",
    label: "Seances",
    path: "sa",
    icon: "stopwatch",
    component: lazy(() => import("./Seance/garderie/seancePage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Seance/garderie/ajoutreSeance.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:SID",
        component: lazy(() => import("./Seance/garderie/infoSeance.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "modifier/:SID",
        component: lazy(() => import("./Seance/garderie/editSeance.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "resumer",
        component: lazy(() => import("./Seance/garderie/resumerSeance.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "salles",
        component: lazy(() => import("./Salles/sallePage.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "salles/info/:SAID",
        component: lazy(() => import("./Salles/salleInfo.js")),
      },
    ]
  },
  examen_garderie: {
    id: "examen_garderie",
    label: "Examen",
    path: "ex",
    icon: "calendar3",
    component: lazy(() => import("./Examen/garderie/examainPage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Examen/garderie/ajoutreExamain.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:EID",
        component: lazy(() => import("./Examen/garderie/infoExamain.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "bultain",
        component: lazy(() => import("./Eleve/ajouterBultin.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "matiere",
        component: lazy(() => import("./Matiere/matierePage.js")),
      },
    ]
  },

  //magazin
  stock: {
    id: "stock",
    label: "Stock",
    path: "sk",
    icon: "upc-scan",
    component: lazy(() => import("./Stock/magazin/stockPage.js")),
    submodules: [
      {
        id: "infor_article",
        label: "Ajouter BS",
        path: "ajouter",
        component: lazy(() => import("./Stock/magazin/addArticle.js")),
      },
      {
        id: "infor_article",
        label: "Ajouter BS",
        path: "info/:code",
        component: lazy(() => import("./Stock/magazin/articleInfo.js")),
      },
      {
        id: "infor_article",
        label: "Ajouter BS",
        path: "famille",
        component: lazy(() => import("./Stock/magazin/famille.js")),
      },
      {
        id: "ajouter_be",
        label: "Ajouter BE",
        path: "be",
        component: lazy(() => import("./Stock/magazin/bonEntre.js")),
      },
      {
        id: "ajouter_be",
        label: "Ajouter BE",
        path: "bs",
        component: lazy(() => import("./Stock/magazin/bonSortie.js")),
      },
    ],
  },
  facture_magazin: {
    id: "facture",
    label: {
      default : "Facture",
      salle_marriage : "Contrat",
    },
    path: "facture",
    icon: "receipt-cutoff",
    component: lazy(() => import("./Factures/magazin/facturePage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Factures/magazin/ajoutreFacture.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:FID",
        component: lazy(() => import("./Factures/magazin/infoFacture.js")),
      },
       {
        id: "factureInfo",
        label: "Info facture",
        path: "resumer",
        component: lazy(() => import("./Factures/magazin/resumerFactures.js")),
      },
    ],
  },
  caisses_magazin: {
    id: "caisses",
    label: "Caisses",
    path: "ca",
    icon: "pc-display-horizontal",
    component: lazy(() => import("./Caisses/magazin/caissePage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Caisses/magazin/ajouterCaisse.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:CID",
        component: lazy(() => import("./Caisses/magazin/caisseInfo.js")),
      },
    ]
  },
  fournisseur: {
    id: "caisses",
    label: "Fournisseur",
    path: "fo",
    icon: "truck",
    component: lazy(() => import("./Fournisseur/fournisseurPage.js")),
     submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Fournisseur/ajouterFournisseur.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:FSID",
        component: lazy(() => import("./Fournisseur/fournisseurInfo.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "calendrier",
        component: lazy(() => import("./Fournisseur/calendarFournisseur.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "recherche",
        component: lazy(() => import("./Fournisseur/rechercheFournisseur.js")),
      },
    ]
  },
  //transporteur
  operations: {
    id: "operations",
    label: "Operations",
    path: "op",
    icon: "send-check",
    component: lazy(() => import("./Operation/operationPage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Operation/ajoutreOperation.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:OPID",
        component: lazy(() => import("./Operation/infoOperation.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "resumer",
        component: lazy(() => import("./Operation/resumerOperation.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "calendar",
        component: lazy(() => import("./Operation/calendar.js")),
      },
      {
        id: "tarification",
        label: "Info facture",
        path: "tarif",
        component: lazy(() => import("./Tarification/forfaitPage.js")),
      },
    ]
  },
  stock_transporteur: {
    id: "stock_transporteur",
    label: "Stock",
    path: "sk",
    icon: "upc-scan",
    component: lazy(() => import("./Stock/transporteur/stockPage.js")),
  },
  camions: {
    id: "camions",
    label: "Camions",
    path: "camion",
    icon: "truck",
    component: lazy(() => import("./Camion/transporteur/camionPage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "ajouter",
        component: lazy(() => import("./Camion/transporteur/addCamion.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:code",
        component: lazy(() => import("./Camion/depot/camionInfo.js")),
        //component: lazy(() => import("./Camion/transporteur/camionInfo.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "fonds",
        component: lazy(() => import("./Camion/depot/ajouterFonds.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "inventaire",
        component: lazy(() => import("./Camion/depot/inventaireCamion.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "map",
        component: lazy(() => import("./Camion/transporteur/camionMap.js")),
      },
    ]
  },

  //avocat
  seance_avocat: {
    id: "seance_avocat",
    label: "Seances",
    path: "sa",
    icon: "stopwatch",
    component: lazy(() => import("./Seance/avocat/seancePage.js")),
    submodules: [
      {
        id: "ajouter_seances_docteur",
        label: "Info Seance",
        path: "ajouter",
        component: lazy(() => import("./Seance/docteur/ajoutreSeance.js")),
      },
      {
        id: "seances_docteur_info",
        label: "Info Seance",
        path: "info/:SID",
        component: lazy(() => import("./Seance/docteur/infoSeance.js")),
      },
      {
        id: "seances_docteur_resumer",
        label: "Info Seance",
        path: "resumer",
        component: lazy(() => import("./Seance/docteur/resumerSeance.js")),
      },
      
      //...
      {
        id: "tarification",
        label: "Info facture",
        path: "tarif",
        component: lazy(() => import("./Tarification/forfaitPage.js")),
      },
    ],
  },
  sujet: {
    id: "sujet",
    label: "Sujet",
    path: "sujet",
    icon: "folder2",
    component: lazy(() => import("./Sujet/sujetPage.js")),
    submodules: [
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "ajouter-c",
        component: lazy(() => import("./Ordonances/ajouterOrdonance.js")),
      },
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "info/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceInfo.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "modifier/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceEdit.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "sk",
        component: lazy(() => import("./Stock/docteur/StockPage.js")),
      },
    ]
  },

  //hotesl
  service: {
    id: "service",
    label: "Services",
    path: "service",
    icon: "plugin",
    component: lazy(() => import("./Seance/avocat/seancePage.js")),
    submodules: [
      {
        id: "ajouter_seances_docteur",
        label: "Info Seance",
        path: "ajouter",
        component: lazy(() => import("./Seance/docteur/ajoutreSeance.js")),
      },
      {
        id: "seances_docteur_info",
        label: "Info Seance",
        path: "info/:SID",
        component: lazy(() => import("./Seance/docteur/infoSeance.js")),
      },
      {
        id: "seances_docteur_resumer",
        label: "Info Seance",
        path: "resumer",
        component: lazy(() => import("./Seance/docteur/resumerSeance.js")),
      },
      
      //...
      {
        id: "tarification",
        label: "Info facture",
        path: "tarif",
        component: lazy(() => import("./Tarification/forfaitPage.js")),
      },
    ],
  },
  chambres: {
    id: "chambres",
    label: "Chambres",
    path: "chambres",
    icon: "door-open",
    component: lazy(() => import("./Sujet/sujetPage.js")),
    submodules: [
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "ajouter-c",
        component: lazy(() => import("./Ordonances/ajouterOrdonance.js")),
      },
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "info/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceInfo.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "modifier/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceEdit.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "sk",
        component: lazy(() => import("./Stock/docteur/StockPage.js")),
      },
    ]
  },

  //courtier
  immobilier : {
    id: "immobilier",
    label: "Immobilier",
    path: "immobilier",
    icon: "buildings",
    component: lazy(() => import("./Sujet/sujetPage.js")),
    submodules: [
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "ajouter-c",
        component: lazy(() => import("./Ordonances/ajouterOrdonance.js")),
      },
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "info/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceInfo.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "modifier/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceEdit.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "sk",
        component: lazy(() => import("./Stock/docteur/StockPage.js")),
      },
    ]
  },

  //courtier
  chantier : {
    id: "chantier",
    label: "Chantier",
    path: "chantier",
    icon: "cone-striped",
    component: lazy(() => import("./Sujet/sujetPage.js")),
    submodules: [
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "ajouter-c",
        component: lazy(() => import("./Ordonances/ajouterOrdonance.js")),
      },
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "info/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceInfo.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "modifier/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceEdit.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "sk",
        component: lazy(() => import("./Stock/docteur/StockPage.js")),
      },
    ]
  },

  //architecte
  projet : {
    id: "projet",
    label: "Projet",
    path: "projet",
    icon: "window-sidebar",
    component: lazy(() => import("./Sujet/sujetPage.js")),
    submodules: [
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "ajouter-c",
        component: lazy(() => import("./Ordonances/ajouterOrdonance.js")),
      },
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "info/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceInfo.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "modifier/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceEdit.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "sk",
        component: lazy(() => import("./Stock/docteur/StockPage.js")),
      },
    ]
  },

  //vg_agence
  voyages: {
    id: "voyages",
    label: "Voyages",
    path: "voyages",
    icon: "airplane-engines",
    component: lazy(() => import("./Finance/financePage.js")),
    submodules: [
      {
        id: "financeStat",
        label: "Stat",
        path: "stat",
        component: lazy(() => import("./Finance/financeStat.jsx")),
      },
    ],
  },
  partenaires: {
    id: "partenaires",
    label: "Partenaires",
    path: "partenaires",
    icon: "diagram-3",
    component: lazy(() => import("./Finance/financePage.js")),
    submodules: [
      {
        id: "financeStat",
        label: "Stat",
        path: "stat",
        component: lazy(() => import("./Finance/financeStat.jsx")),
      },
    ],
  },

  //coiffure
  calendrier_coiffure : {
    id: "calendrier_coiffure",
    label: "Calendirer",
    path: "calendrier",
    icon: "calendar-week",
    component: lazy(() => import("./Finance/financePage.js")),
    submodules: [
      {
        id: "financeStat",
        label: "Stat",
        path: "stat",
        component: lazy(() => import("./Finance/financeStat.jsx")),
      },
    ],
  },

  //comptable
  operations_comptable: {
    id: "operations_comptable",
    label: "Operations",
    path: "op",
    icon: "calculator",
    component: lazy(() => import("./Ordonances/ordonancePage.js")),
    submodules: [
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "ajouter-c",
        component: lazy(() => import("./Ordonances/ajouterOrdonance.js")),
      },
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "info/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceInfo.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "modifier/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceEdit.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "sk",
        component: lazy(() => import("./Stock/docteur/StockPage.js")),
      },
    ]
  },
  archive: {
    id: "archive",
    label: "Archive",
    path: "archive",
    icon: "archive",
    component: lazy(() => import("./Ordonances/ordonancePage.js")),
    submodules: [
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "ajouter-c",
        component: lazy(() => import("./Ordonances/ajouterOrdonance.js")),
      },
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "info/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceInfo.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "modifier/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceEdit.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "sk",
        component: lazy(() => import("./Stock/docteur/StockPage.js")),
      },
    ]
  },
  missions: {
    id: "missions",
    label: "Missions",
    path: "missions",
    icon: "check2-square",
    component: lazy(() => import("./Ordonances/ordonancePage.js")),
    submodules: [
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "ajouter-c",
        component: lazy(() => import("./Ordonances/ajouterOrdonance.js")),
      },
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "info/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceInfo.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "modifier/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceEdit.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "sk",
        component: lazy(() => import("./Stock/docteur/StockPage.js")),
      },
    ]
  },

  //location 
  locations : {
    id: "locations",
    label: "Locations",
    path: "locations",
    icon: "hand-index-thumb",
    component: lazy(() => import("./Ordonances/ordonancePage.js")),
    submodules: [
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "ajouter-c",
        component: lazy(() => import("./Ordonances/ajouterOrdonance.js")),
      },
      {
        id: "ajouter_ordonance",
        label: "Info Seance",
        path: "info/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceInfo.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "modifier/:ORID",
        component: lazy(() => import("./Ordonances/ordonanceEdit.js")),
      },
      {
        id: "modifier_ordonance",
        label: "Info Seance",
        path: "sk",
        component: lazy(() => import("./Stock/docteur/StockPage.js")),
      },
    ]
  },

  // used
  profile: {
    id: "profile",
    label: "Profile",
    path: "profile",
    icon: "💰",
    component: lazy(() => import("./Used/Profile/profilePage.js")),
  },
  parametre: {
    id: "parametre",
    label: "Parametre",
    path: "parametre",
    icon: "💰",
    component: lazy(() => import("./Used/Setting/settingPage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "paymment",
        component: lazy(() => import("./Used/Setting/paymment.js")),
      },
    ],
  },
  messages: {
    id: "messages",
    label: "Messages",
    path: "messages",
    icon: "💰",
    component: lazy(() => import("./Used/Messages/messagesPage.js")),
  },
  forum: {
    id: "forum",
    label: "Forum",
    path: "forum",
    icon: "💰",
    component: lazy(() => import("./Used/Forum/notificationPage.js")),
  },
  tools: {
    id: "tools",
    label: "Outils",
    path: "tools",
    icon: "tools",
    component: lazy(() => import("./Used/Tools/toolsPage")),
     submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "store",
        component: lazy(() => import("./Used/Tools/abyedhStore.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "bmi",
        component: lazy(() => import("./Used/Tools/apps/health/bmi-calc.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "bmr",
        component: lazy(() => import("./Used/Tools/apps/health/bmr-calc.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "dose-c",
        component: lazy(() => import("./Used/Tools/apps/health/dose-calc.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "card-c",
        component: lazy(() => import("./Used/Tools/apps/health/cardiaque-calc.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "convertisseur-med",
        component: lazy(() => import("./Used/Tools/apps/health/convertisseur-med.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "prescription",
        component: lazy(() => import("./Used/Tools/apps/health/prescription.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "r-recette",
        component: lazy(() => import("./Used/Tools/apps/restauration/fiche-recette.js")),
      },
      {
        id: "factureInfo",
        label: "Info facture",
        path: "menu",
        component: lazy(() => import("./Used/Tools/apps/restauration/menu.js")),
      },
    ]
  },
  support: {
    id: "support",
    label: "Support",
    path: "support",
    icon: "💰",
    component: lazy(() => import("./Used/Documentation/documentationPage.js")),
  },
};

/**
 * Resolves a list of module ids (as returned by the backend) into
 * registry entries, silently ignoring any id the frontend doesn't
 * know about (defensive — a backend could ship a module id before
 * the frontend build that supports it is deployed).
 */
export function resolveModules(moduleIds = []) {
  return moduleIds.map((id) => MODULE_REGISTRY[id]).filter(Boolean);
}
